import React from 'react';
import { MessageSquare } from 'lucide-react';
import { useAnnotations } from './AnnotationProvider';
import {
  getConversationAnnotationCount,
  getRunAnnotationCount,
  getSpanAnnotationCount,
  spanCountKey,
} from './annotationSelectors';

export function AnnotationCount({ count }) {
  if (!count) return null;
  return <span className="tabular-nums">{count}</span>;
}

/**
 * Compact table/list indicator. Hidden when the count is 0 so the existing
 * layout is undisturbed. Click opens the matching drawer on Annotations.
 */
export default function AnnotationBadge({
  conversationId,
  runId,
  spanId,
  spanStartedAt,
  count: countOverride,
  onClick,
  title,
}) {
  const { enabled, annotations, counts } = useAnnotations();
  if (!enabled) return null;

  const fromCounts = conversationId
    ? counts.conversations[conversationId]
    : runId
      ? counts.runs[runId]
      : counts.spans[spanCountKey(spanId, spanStartedAt)];

  const count = countOverride != null
    ? countOverride
    : fromCounts != null
      ? fromCounts
      : conversationId
        ? getConversationAnnotationCount(annotations, conversationId)
        : runId
          ? getRunAnnotationCount(annotations, runId)
          : getSpanAnnotationCount(annotations, spanId, spanStartedAt);

  if (!count) return null;

  const label = title || `${count} annotation${count === 1 ? '' : 's'}`;
  const inner = (
    <>
      <MessageSquare size={11} strokeWidth={2} className="flex-none" />
      <AnnotationCount count={count} />
    </>
  );

  const className =
    'inline-flex h-5 items-center gap-0.5 rounded px-1 text-[10px] font-semibold tabular-nums text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]';

  if (typeof onClick !== 'function') {
    return (
      <span title={label} className={className}>
        {inner}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={className}
    >
      {inner}
    </button>
  );
}
